import { useCallback, useEffect, useRef } from "react"

export interface KeyboardShortcut {
  key: string
  modifiers?: {
    shift?: boolean
    ctrl?: boolean
    meta?: boolean
    alt?: boolean
  }
  handler?: () => void
  description: string
  category: "navigation" | "actions" | "global"
  when?: () => boolean
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[]
  enabled?: boolean
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  const tagName = target.tagName
  return (
    tagName === "INPUT" || tagName === "TEXTAREA" || target.isContentEditable
  )
}

function isInsideDialog(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  return target.closest('[role="dialog"]') !== null
}

function matchesShortcut(
  e: KeyboardEvent,
  shortcut: KeyboardShortcut,
): boolean {
  const { key, modifiers = {} } = shortcut

  const needsShift = modifiers.shift ?? false
  const needsCtrl = modifiers.ctrl ?? false
  const needsMeta = modifiers.meta ?? false
  const needsAlt = modifiers.alt ?? false

  if (e.ctrlKey !== needsCtrl) return false
  if (e.metaKey !== needsMeta) return false
  if (e.altKey !== needsAlt) return false

  if (key === "?") {
    return e.key === "?" && e.shiftKey
  }

  if (e.shiftKey !== needsShift) return false

  if (e.key.toLowerCase() !== key.toLowerCase()) {
    return false
  }

  return true
}

export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
}: UseKeyboardShortcutsOptions) {
  const shortcutsRef = useRef(shortcuts)

  useEffect(() => {
    shortcutsRef.current = shortcuts
  }, [shortcuts])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return
      if (isEditableElement(e.target)) return
      if (isInsideDialog(e.target)) return

      for (const shortcut of shortcutsRef.current) {
        if (matchesShortcut(e, shortcut)) {
          if (shortcut.when && !shortcut.when()) continue

          e.preventDefault()
          shortcut.handler?.()
          return
        }
      }
    },
    [enabled],
  )

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])
}

export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const parts: string[] = []

  if (shortcut.modifiers?.ctrl) parts.push("Ctrl")
  if (shortcut.modifiers?.meta) parts.push("⌘")
  if (shortcut.modifiers?.alt) parts.push("Alt")
  if (shortcut.modifiers?.shift) parts.push("Shift")

  let keyDisplay = shortcut.key
  if (shortcut.key.length === 1) {
    keyDisplay = shortcut.key.toUpperCase()
  }
  parts.push(keyDisplay)

  return parts.join("+")
}
